import {toIntent, provider, forEachIntent} from '../intent';
import type {Intent} from '../intent';
import {assignTokensIntent} from '../token/assignTokens';

/**
 * Non-enumerable symbol under which a theme's metadata is stored on the theme
 * intent. Non-enumerable so it never interferes with intent merging or
 * iteration. Read it through {@link DO_NOT_USE_getThemeMetadata} rather than
 * accessing it directly.
 */
const $theme = Symbol('theme');

/** The set of things a theme customizes, derived from its intents. */
export interface ThemeCustomizes {
  /** Whether the theme reassigns any Sail tokens. */
  tokens: boolean;
  /** The names of the Sail components the theme customizes, e.g. `['Button']`. */
  components: string[];
}

/** Metadata describing a theme, carried on the theme intent itself. */
export interface ThemeMetadata {
  name: string;
  description: string;
  /** Derived from the theme's intents; never hand-written. */
  customizes: ThemeCustomizes;
}

/** Reads the metadata off a theme intent, or `undefined` if it is not a theme. */
export function DO_NOT_USE_getThemeMetadata(
  intent: Intent,
): ThemeMetadata | undefined {
  return (intent as {[$theme]?: ThemeMetadata})[$theme];
}

/**
 * Walks a theme's intents to derive what it customizes:
 * - `tokens`: true if any intent is a token reassignment.
 * - `components`: the deduped names of every component config the theme
 *   overrides (component config intents are `provider` intents whose context
 *   is the component's config, tagged with `options.name`).
 *
 * `forEachIntent` flattens compound (`toIntent`) intents, so customizations
 * supplied through `toIntent([...])` are classified by their leaves. Nested
 * themes are themselves compound intents, so they flatten too and composed
 * themes report the union.
 */
function derive(intents: Intent[]): ThemeCustomizes {
  let tokens = false;
  const components = new Set<string>();

  forEachIntent(intents, (intent) => {
    if (assignTokensIntent.isIntent(intent)) {
      tokens = true;
    } else if (provider.isIntent(intent)) {
      const [context] = intent as unknown as [
        {options?: {name?: string}},
        unknown,
      ];
      const name = context?.options?.name;
      if (typeof name === 'string') {
        components.add(name);
      }
    }
  });

  return {tokens, components: [...components]};
}

/** Merges the intents into a single intent and attaches theme metadata to it. */
function toThemeIntent(intents: Intent[], metadata: ThemeMetadata): Intent {
  const intent = toIntent(intents);
  Object.defineProperty(intent, $theme, {
    value: metadata,
    enumerable: false,
    writable: false,
  });
  return intent;
}

/**
 * Creates a theme: a plain {@link Intent} that bundles the given
 * customizations and carries its own metadata. Because it is just an intent,
 * it applies wherever intents are consumed (`uses={[theme]}`) and composes with
 * other themes.
 *
 * `customizes` is derived from the intents, so it can never drift from what the
 * theme actually does.
 */
export function DO_NOT_USE_createTheme(
  meta: {name: string; description: string},
  intents: Intent[],
): Intent {
  return toThemeIntent(intents, {...meta, customizes: derive(intents)});
}
